import { Button, ButtonProps } from '@chakra-ui/react';
import { autoUpdate, FloatingPortal, offset, shift, size, useFloating } from '@floating-ui/react';
import { memo, useCallback } from 'react';

import CreateOrgPopover from '@features/onboarding/CreateOrgPopover';
import { useBoolean } from 'hooks/useBoolean';
import { useCreateOrg } from 'hooks/useCreateOrg';
import { IUserOrg } from 'stores/orgStore';
import Plus from 'vectors/Plus';

interface Props {
  onOrgCreated?: (org: IUserOrg) => void;
  title?: string;
  placeholder?: string;
  createMenuLabel?: string;
  size?: ButtonProps['size'];
  borderRadius?: ButtonProps['borderRadius'];
  popoverMinWidth?: number;
}

const CreateOrgButton = ({
  onOrgCreated,
  title = 'Start new business',
  placeholder,
  createMenuLabel,
  size: buttonSize,
  borderRadius: buttonBorderRadius,
  popoverMinWidth = 0,
}: Props) => {
  const [isAdding, setIsAdding] = useBoolean();
  const { refs, floatingStyles } = useFloating({
    open: isAdding,
    strategy: 'fixed',
    onOpenChange: setIsAdding.toggle,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(({ rects }) => {
        return -rects.reference.height / 2 - rects.floating.height / 2;
      }),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            minWidth: `${Math.max(rects.reference.width, popoverMinWidth)}px`,
          });
        },
      }),
      shift({ padding: 8 }),
    ],
  });

  const onCancel = useCallback(() => {
    setIsAdding.off();
  }, [setIsAdding]);

  const { createOrg } = useCreateOrg({
    onCancel,
    onOrgCreated,
  });

  return (
    <>
      <Button
        ref={refs.setReference}
        variant="solid"
        onClick={setIsAdding.on}
        size={buttonSize}
        borderRadius={buttonBorderRadius}
      >
        <Plus /> {title}
      </Button>
      {isAdding && (
        <FloatingPortal>
          <CreateOrgPopover
            setFloatingReference={refs.setFloating}
            floatingStyles={floatingStyles}
            onCancel={onCancel}
            onCreateOrg={createOrg}
            placeholder={placeholder}
            createMenuLabel={createMenuLabel}
          />
        </FloatingPortal>
      )}
    </>
  );
};

export default memo(CreateOrgButton);
